/**
 * useRecoverRejectedPfV2Session — mints a new session when the backend refuses
 * the one we hold.
 *
 * A session the backend does not have is indistinguishable from someone else's:
 * the ACL resolves `SESSION` through `isRelatedToSession(user, id)`, which simply
 * answers false for an id it cannot find, so the catalogue comes back **403**,
 * not 404. Reproduced against the purchase-flow preview: a live session id
 * returns 200 with its product groups, an unknown one returns 403 with an empty
 * body, for the same user, workspace and token.
 *
 * `useEnsurePfV2Session` only creates a session when it holds none, so before
 * this hook a client in that state stayed on the catalogue's error screen for as
 * long as the tab was open — Retry re-ran the same doomed request, and only a
 * reload cleared it. Discarding the id is what lets the ensure-hook mint a
 * replacement.
 *
 * Bounded to ONE recovery per visit (`draft.sessionRecoveryAttempted`). A 403 can
 * also mean the client genuinely may not order in this workspace — the same
 * endpoint checks `requireCanOrder` — and re-minting forever would turn that into
 * an invisible loop instead of an error the client can act on.
 */

import { useEffect } from 'react'
import type { AxiosError } from 'axios'

import { usePfCatalogue } from 'dataQueries/purchaseFlowRevamp.query'
import { logger } from 'utils/logging'

import { usePfStore } from '../store'

/** HTTP status of a react-query error, when it is an Axios one. */
const statusOf = (error: unknown): number | undefined =>
  (error as AxiosError | undefined)?.response?.status

/** The statuses that mean "this session is not yours to read". */
const REJECTED = new Set([403, 404])

export function useRecoverRejectedPfV2Session(): void {
  const sessionId = usePfStore((s) => s.draft.sessionId)
  const discardRejectedSession = usePfStore((s) => s.discardRejectedSession)

  // Same query key the catalogue already uses, so this subscribes to the existing
  // result rather than issuing a request of its own.
  const { error } = usePfCatalogue()

  const status = statusOf(error)

  useEffect(() => {
    if (!sessionId || status === undefined || !REJECTED.has(status)) return

    if (discardRejectedSession()) {
      logger.warn('pf-v2 session rejected by the backend; discarding it to open a new one', undefined, {
        context: {
          component: 'useRecoverRejectedPfV2Session',
          action: 'discardRejectedSession',
          metadata: { sessionId, status },
        },
      })
    }
  }, [sessionId, status, discardRejectedSession])
}
